<script setup>
/**
 * The month on screen against the one before it: one card per direction —
 * money in, money out, money set aside — each with its total and its biggest
 * category, and how both moved. The figures are `monthComparison`'s.
 *
 * The arrow says which way a number went; its colour says whether that was
 * good news (`trendTone`): more spending or less income or savings is red, or
 * amber when it moved by less than 10%, and the opposite is green.
 */
import { computed } from 'vue'
import Icon from './ui/Icon.vue'
import SectionTitle from './ui/SectionTitle.vue'
import { isHidden } from '../stores/sections'
import { fmtGlobal as fmt, isCurrentMonth, monthComparison } from '../stores'
import { monthLabel } from '../lib/dates'
import { trendTone } from '../lib/compare'
import { t } from '../stores/i18n'

const KINDS = {
  income: { icon: 'in', label: 'compare.income' },
  spent: { icon: 'out', label: 'compare.spent' },
  saved: { icon: 'target', label: 'compare.saved' },
}

const ARROW = { up: '↑', down: '↓', same: '→' }
const TONE = { good: 'text-accent', warn: 'text-warn', danger: 'text-danger', neutral: 'text-muted' }

const title = computed(() => {
  const m = monthComparison.value
  if (!m || isCurrentMonth.value) return t('compare.title')
  return t('compare.titleThen', { month: monthLabel(m.month), previous: monthLabel(m.previous) })
})

/** "last month", or "the month before" when the month on screen is not this one. */
const than = computed(() => (isCurrentMonth.value ? t('compare.lastMonth') : t('compare.monthBefore')))

const trend = (kind, figure) => ({
  arrow: ARROW[figure.direction] ?? '',
  tone: figure.direction === 'none' ? 'text-faint' : TONE[trendTone(kind, figure)],
  text: t(`compare.${figure.direction}`, {
    // A percentage is shown in privacy mode: it says how things moved, never how much money.
    percent: figure.percent,
    than: than.value,
  }),
})

const signed = (diff) => (diff > 0 ? `+${fmt(diff, { round: true })}` : fmt(diff, { round: true }))

const cards = computed(() => {
  const m = monthComparison.value
  if (!m) return []
  return m.totals.map((total, i) => {
    const top = m.tops[i]
    return {
      kind: total.kind,
      ...KINDS[total.kind],
      total,
      totalTrend: trend(total.kind, total),
      top,
      topName: top.key === null ? null : top.key || t('charts.uncategorised'),
      topTrend: trend(top.kind, top),
    }
  })
})

/** Nothing in or out in either month is nothing to compare. */
const shown = computed(() => cards.value.some((c) => c.total.now > 0 || c.total.before > 0))
</script>

<template>
  <section v-if="shown" class="min-w-0" :class="isHidden('compare') ? '' : 'w-full'" data-compare data-section="compare">
    <SectionTitle id="compare" :title="title" />
    <div v-if="!isHidden('compare')" class="grid grid-cols-1 gap-2.5 sm:gap-3 md:grid-cols-3">
      <div v-for="card in cards" :key="card.kind" class="card flex flex-col p-4" :data-compare-kind="card.kind">
        <p class="flex items-center gap-1.5 text-[12px] font-medium text-muted">
          <Icon :name="card.icon" :size="14" /> {{ t(card.label) }}
        </p>
        <p class="tnum mt-1.5 text-2xl leading-none font-bold">{{ fmt(card.total.now, { round: true }) }}</p>
        <p class="mt-2 text-[12.5px] font-medium" :class="card.totalTrend.tone" data-trend>
          {{ card.totalTrend.text }}
          <span v-if="card.totalTrend.arrow" aria-hidden="true">{{ card.totalTrend.arrow }}</span>
        </p>
        <!-- Every line is always there, blank when it has nothing to say, so the
             three cards stay one height and their halves line up. -->
        <p class="tnum mt-0.5 text-[12px] text-faint" data-was>
          <template v-if="card.total.before > 0">
            {{ t('compare.was', { amount: fmt(card.total.before, { round: true }) }) }} · {{ signed(card.total.diff) }}
          </template>
          <span v-else aria-hidden="true">&nbsp;</span>
        </p>

        <div class="mt-3 border-t border-line-soft pt-3" data-top>
          <p class="text-[12px] font-medium text-muted">{{ t('compare.top') }}</p>
          <template v-if="card.topName !== null">
            <p class="mt-1 flex items-baseline justify-between gap-3">
              <span class="min-w-0 truncate text-[14px] font-semibold" :title="card.topName">{{ card.topName }}</span>
              <span class="tnum shrink-0 text-[14px] font-semibold">{{ fmt(card.top.now, { round: true }) }}</span>
            </p>
            <p class="mt-1 text-[12.5px] font-medium" :class="card.topTrend.tone" data-trend>
              {{ card.topTrend.text }}
              <span v-if="card.topTrend.arrow" aria-hidden="true">{{ card.topTrend.arrow }}</span>
            </p>
          </template>
          <template v-else>
            <p class="mt-1 text-[14px] text-faint">{{ t('compare.nothing') }}</p>
            <p class="mt-1 text-[12.5px]" aria-hidden="true">&nbsp;</p>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>
